/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import { type ColumnDef, type SelectOption, CAPACITY_UNIT, EventService, I18nService, TimeService, UNPLACEHOLDER, UtilService } from '@dm/core';
import { healthStatusE, MO, runningStatusE, TIME_FORMATTER } from 'app/common/constants';
import { DiskTypeE } from 'app/common/constants/disk-type';
import { GlobalDataService, ToolService } from 'app/common/services';
import { type LifPort } from 'app/common/types/mo';
import { SWITCH } from 'app/config';
import { FeatureConfig } from 'app/config/feature';

import { HardwareStrService } from '../../hardware-str.service';
import { HardwareDetailService } from '../detail/detail.service';
import { TEMPERATURE_INVALID_VALUE } from '../device.const';

const TO_BE_WORK = 110;
const compareNum = (a: string, b: string): number => (Number(a) - Number(b));

@Injectable({
    providedIn: 'root',
})
export class ColumnDefsService {
    i18nLabel = this.str.i18nLabel;
    healthStatusOpt = this.generateOpt(healthStatusE);
    healthStatusOptByPort = {
        sas: this.genOptByValues(healthStatusE, [1, 2]),
        eth: this.genOptByValues(healthStatusE, [0, 1, 2, 7]),
        fc: this.genOptByValues(healthStatusE, [0, 1, 2, 7]),
        bond: this.genOptByValues(healthStatusE, [0, 1, 2, 4, 7]),  // 部分损害 -> 4
        serial: this.genOptByValues(healthStatusE, [0, 1, 2, 3, 9]),
        // 管理端口和串口和并集
        manageAndSerial: this.genOptByValues(healthStatusE, [0, 1, 2, 3, 7, 9]),
    };
    // 覆盖110枚举，备用 => 待工作
    runningStatusOpt = {
        ...this.generateOpt(runningStatusE),
        [TO_BE_WORK]: this.i18nService.get('common.EnumDefine_RUNNING_STATUS_E_TO_BE_WORK'),
    };

    /**
     * 微存储用“主端口”
     */
    portName = FeatureConfig.IsHideLogicPortCurrentPort ? this.i18nLabel.homePort : this.i18nLabel.port;

    name: ColumnDef = {
        field: 'NAME',
        header: this.i18nLabel.name,
        columnFilter: true,
        filterType: 'search',
        sortable: true,
        width: 100,
    };

    hiddenName: ColumnDef = {
        field: 'NAME',
        header: this.i18nLabel.name,
        columnFilter: true,
        filterType: 'search',
        sortable: true,
        width: 100,
        hidden: true,
    };

    nameLink: ColumnDef = {
        ...this.name,
        onClick: (data) => this.event.emit('table.property', data),
    };

    id: ColumnDef = {
        field: 'ID',
        header: 'ID',
        width: '1rem',
        columnFilter: true,
        sortable: true,
        hidden: true,
    };

    location: ColumnDef = {
        field: 'LOCATION',
        header: this.i18nLabel.location,
        sortable: true,
        columnFilter: true,
        width: 150,
        filterType: 'search',
    };

    locationLink: ColumnDef = {
        ...this.location,
        onClick: (data) => this.event.emit('table.property', data),
    };

    healthStatus: ColumnDef = {
        field: 'HEALTHSTATUS',
        header: this.i18nLabel.healthStatus,
        filterType: 'dropdown',
        options: this.healthStatusOptByPort.eth,
        render: (value) => this.healthStatusOpt[value],
        sortable: true,
        columnFilter: true,
    };

    healthStatusByPort = {
        sas: {
            ...this.healthStatus,
            options: this.healthStatusOptByPort.sas,
        },
        eth: {
            ...this.healthStatus,
            options: this.healthStatusOptByPort.eth,
        },
        fc: {
            ...this.healthStatus,
            options: this.healthStatusOptByPort.fc,
        },
        bond: {
            ...this.healthStatus,
            options: this.healthStatusOptByPort.bond,
        },
        serial: {
            ...this.healthStatus,
            options: this.healthStatusOptByPort.serial,
        },
        manageAndSerial: {
            ...this.healthStatus,
            options: this.healthStatusOptByPort.manageAndSerial,
        },
    };

    runningStatus: ColumnDef = {
        field: 'RUNNINGSTATUS',
        header: this.i18nLabel.runningStatus,
        filterType: 'dropdown',
        options: this.str.ethPortRunningStatusOptions,
        render: (value) => this.runningStatusOpt[value],
        sortable: true,
        columnFilter: true,
    };

    /**
     * 健康状态公共定义，需要自己定义options
     */
    healthStatusCommon: ColumnDef = {
        field: 'HEALTHSTATUS',
        header: this.i18nLabel.healthStatus,
        filterType: 'dropdown',
        render: (value) => this.healthStatusOpt[value] || UNPLACEHOLDER,
        sortable: true,
        columnFilter: true,
    };

    /**
     * 运行状态公共定义，需要自己定义options
     */
    runningStatusCommon: ColumnDef = {
        field: 'RUNNINGSTATUS',
        header: this.i18nLabel.runningStatus,
        filterType: 'dropdown',
        render: (value) => this.runningStatusOpt[value] || UNPLACEHOLDER,
        sortable: true,
        columnFilter: true,
    };

    /**
     * 逻辑端口-运行状态
     */
    lifPortRunningStatus: ColumnDef = {
        ...this.runningStatusCommon,
        options: this.str.lifPortRunningStatusOptions,
    };

    /**
     * 框-运行状态
     */
    enclosureRunningStatus: ColumnDef = {
        ...this.runningStatusCommon,
        options: this.str.enclosureRunningStatusOptions,
    };

    /**
     * 框-健康状态
     */
    enclosureHealthStatus: ColumnDef = {
        ...this.healthStatusCommon,
        options: this.str.enclosureHealthStatusOptions,
    };

    /**
     * 控制器-运行状态
     */
    controllerRunningStatus: ColumnDef = {
        ...this.runningStatusCommon,
        options: this.str.controllerRunningStatusOptions,
    };

    /**
     * 控制器-健康状态
     */
    controllerHealthStatus: ColumnDef = {
        ...this.healthStatusCommon,
        options: this.str.controllerHealthStatusOptions,
    };

    /**
     * 管理板-健康状态
     */
    mgmtHealthStatus: ColumnDef = {
        ...this.healthStatusCommon,
        options: this.str.mgmtHealthStatusOptions,
    };

    /**
     * 管理板-运行状态
     */
    mgmtRunningStatus: ColumnDef = {
        ...this.runningStatusCommon,
        options: this.str.mgmtRunningStatusOptions,
    };

    /**
     * 计算节点-健康状态
     */
    computeNodeHealthStatus: ColumnDef = {
        ...this.healthStatusCommon,
        options: this.str.computeNodetHealthStatusOptions,
    };

    /**
     * 计算节点-运行状态
     */
    computeNodeRunningStatus: ColumnDef = {
        ...this.runningStatusCommon,
        options: this.str.computeNodeRunningStatusOptions,
    };

    /**
     * cpu-健康状态
     */
    cpuHealthStatus: ColumnDef = {
        ...this.healthStatusCommon,
        options: this.str.cpuHealthStatusOptions,
    };

    /**
     * 内存-健康状态
     */
    memoryHealthStatus: ColumnDef = {
        ...this.healthStatusCommon,
        options: this.str.memoryHealthStatusOptions,
    };

    /**
     * 以太网端口-运行状态
     */
    ethPortRunningStatus: ColumnDef = {
        ...this.runningStatusCommon,
        options: this.str.ethPortRunningStatusOptions,
    };

    /**
     * 以太网端口-健康状态
     */
    ethPortHealthStatus: ColumnDef = {
        ...this.healthStatusCommon,
        options: this.str.ethPortHealthStatusOptions,
    };

    /**
     * FC端口-运行状态
     */
    fcPortRunningStatus: ColumnDef = {
        ...this.runningStatusCommon,
        options: this.str.fcPortRunningStatusOptions,
    };

    /**
     * FC端口-健康状态
     */
    fcPortHealthStatus: ColumnDef = {
        ...this.healthStatusCommon,
        options: this.str.fcPortHealthStatusOptions,
    };

    /**
     * bond端口-运行状态
     */
    bondPortRunningStatus: ColumnDef = {
        ...this.runningStatusCommon,
        options: this.str.bondPortRunningStatusOptions,
    };

    /**
     * bond端口-健康状态
     */
    bondPortHealthStatus: ColumnDef = {
        ...this.healthStatusCommon,
        options: this.str.bondPortHealthStatusOptions,
    };

    /**
     * VLAN-运行状态
     */
    vlanRunningStatus: ColumnDef = {
        ...this.runningStatusCommon,
        options: this.str.vlanRunningStatusOptions,
    };

    ipv4AndMask: ColumnDef = {
        field: 'IPV4ADDR',
        header: this.i18nLabel.ipv4AndMask,
        columnFilter: true,
        filterType: 'search',
        sortable: true,
        render: (value, col, data) => value ? `${value}/${data.IPV4MASK}` : UNPLACEHOLDER,
    };

    ipv6AndPrefix: ColumnDef = {
        field: 'IPV6ADDR',
        header: this.i18nLabel.ipv6AndPrefix,
        columnFilter: true,
        filterType: 'search',
        sortable: true,
        render: (value, col, data) => value ? `${value}/${data.IPV6MASK}` : UNPLACEHOLDER,
    };

    workSpeed: ColumnDef = {
        field: 'RUNSPEED',
        header: this.i18nLabel.workSpeed,
        columnFilter: true,
        sortable: true,
        render: (value) => this.detailService.renderRunningSpeed(value),
        align: 'right',
    };

    maxSpeed: ColumnDef = {
        field: 'MAXSPEED',
        header: this.i18nLabel.maxSpeed,
        columnFilter: true,
        sortable: true,
        render: (value) => this.detailService.renderRunningSpeed(value),
        align: 'right',
    };

    macAddress: ColumnDef = {
        field: 'MACADDRESS',
        header: this.i18nLabel.macAddress,
        sortable: true,
        columnFilter: true,
        filterType: 'search',
    };

    ethSpeed: ColumnDef = {
        field: 'SPEED',
        header: this.i18nLabel.workSpeed,
        columnFilter: true,
        sortable: true,
        compare: compareNum,
        render: (value) => this.detailService.renderRunningSpeed(value),
        align: 'right',
    };

    ethMaxSpeed: ColumnDef = {
        field: 'maxSpeed',
        header: this.i18nLabel.maxSpeed,
        columnFilter: true,
        sortable: true,
        compare: compareNum,
        render: (value) => this.detailService.renderRunningSpeed(value),
        align: 'right',
    };

    portSwitch: ColumnDef = {
        field: 'PORTSWITCH',
        header: this.i18nLabel.portState,
        columnFilter: true,
        sortable: true,
        render: (value) => this.str.portSwitchOpt[value],
    };

    initiatorNum: ColumnDef = {
        field: 'numberOfInitiators',
        header: this.i18nLabel.initiatorNum,
        columnFilter: true,
        // sortable: true,
        compare: compareNum,
        align: 'right',
    };

    portId: ColumnDef = {
        field: 'ID',
        header: this.i18nLabel.portId,
        columnFilter: true,
        hidden: true,
    };

    nameForError: ColumnDef = {
        field: 'NAME',
        header: this.i18nLabel.name,
        columnFilter: true,
        sortable: true,
        hidden: true,
    };

    linkStatus: ColumnDef = {
        header: this.i18nLabel.linkStatus,
        field: 'RUNNINGSTATUS',
        sortable: true,
        render: (value) => this.runningStatusOpt[value],
        columnFilter: true,
    };

    startTime: ColumnDef = {
        field: 'STARTTIME',
        header: this.i18nLabel.startTime,
        columnFilter: true,
        sortable: true,
        compare: compareNum,
        render: (value) => this.renderTime(value),
    };

    mtu: ColumnDef = {
        field: 'MTU',
        header: this.i18nLabel.maxUnit,
        columnFilter: true,
        sortable: true,
        compare: compareNum,
        align: 'right',
        render: (value) => value || UNPLACEHOLDER,
    };

    columnDefs: { [key: string]: ColumnDef[] } = {
        fcPort: this.getFcPortColumnDefs(),
        ethPort: [
            this.hiddenName,
            this.locationLink,
            this.ethPortHealthStatus,
            this.ethPortRunningStatus,
            this.macAddress,
            this.ipv4AndMask,
            this.ipv6AndPrefix,
            {
                ...this.ethSpeed,
                filterType: 'equalSearch',
            },
            {
                ...this.ethMaxSpeed,
                filterType: 'equalSearch',
            },
            {
                ...this.mtu,
                filterType: 'equalSearch',
            },
            {
                field: 'BONDNAME',
                header: this.i18nLabel.bindName,
                sortable: true,
                columnFilter: true,
                render: (value): string => value || UNPLACEHOLDER,
            },
            {
                field: 'LOGICTYPE',
                header: this.i18nLabel.logicType,
                columnFilter: true,
                sortable: true,
                filterType: 'dropdown',
                options: FeatureConfig.IsSupportOceanMicroLogicTypeOpt ? this.str.logicTypeMicroOpt : this.str.logicTypeOpt,
                render: (value): string => FeatureConfig.IsSupportOceanMicroLogicTypeOpt ?
                    this.str.logicTypeMicroOpt[value] : this.str.logicTypeOpt[value] || UNPLACEHOLDER,
            },
            this.portSwitch,
            // A800、微存储直通屏蔽iscsi
            {
                ...this.initiatorNum,
                ignore: SWITCH.IS_DIRECT_MODE || !this.tool.supportSmartThinLicense(),
            },
        ],
        rocePort: [
            this.hiddenName,
            this.locationLink,
            this.ethPortHealthStatus,
            this.ethPortRunningStatus,
            this.macAddress,
            this.ethSpeed,
            this.ethMaxSpeed,
            {
                ...this.mtu,
                filterType: 'equalSearch',
            },
            {
                field: 'LOGICTYPE',
                header: this.i18nLabel.logicType,
                columnFilter: true,
                sortable: true,
                filterType: (FeatureConfig.IsSupportVM && this.tool.hasContainerEntryLicense()) ? 'dropdown' : null,
                options: (FeatureConfig.IsSupportVM && this.tool.hasContainerEntryLicense()) ? this.str.roceLogicTypeVmOpt : this.str.logicTypeOpt,
                render: (value): string => this.str.logicTypeOpt[value] || UNPLACEHOLDER,
            },
            this.portSwitch,
            // A800屏蔽iscsi
            {
                ...this.initiatorNum,
                ignore: !this.tool.supportSmartThinLicense(),
            },
            {
                field: 'snsdSwitch',
                header: this.i18nLabel.snsdBtn,
                columnFilter: true,
                compare: compareNum,
                align: 'right',
                render: (value): string => value !== '0' ? this.str.i18nLabel.snsdBtnOn : this.str.i18nLabel.snsdBtnOff,
            },
            {
                field: 'trust',
                header: this.i18nLabel.trust,
                columnFilter: true,
                render: (value): string => this.str.trustOpt[value] || UNPLACEHOLDER,
                ignore: !FeatureConfig.IsSupportDscpTrustMode,
            },
        ],
        managePort: [
            {
                field: 'TYPE',
                header: this.i18nLabel.type,
                render: (value, clo, data): string => {
                    if (value === MO.SERIAL_PORT) {
                        return this.i18nLabel.serialPort;
                    }
                    if (Number(data.LOGICTYPE) === 2) {
                        return this.i18nLabel.managePort;
                    }
                    if (Number(data.LOGICTYPE) === 4) {
                        return this.i18nLabel.maintenancePort;
                    }

                    return UNPLACEHOLDER;
                },
                sortable: true,
                columnFilter: true,
            },
            this.nameLink,
            this.location,
            this.id,
            this.healthStatusByPort.eth,
            this.runningStatus,
            this.ipv4AndMask,
            this.ipv6AndPrefix,
            this.ethMaxSpeed,
            this.ethSpeed,
            this.portSwitch,
        ],
        sasPort: [
            this.nameLink,
            this.location,
            this.id,
            this.healthStatusByPort.sas,
            {
                ...this.runningStatus,
                options: this.str.sasRunningStatusOpt,
            },
            {
                field: 'WWN',
                header: this.i18nLabel.WWPN,
                columnFilter: FeatureConfig.IsSupportFC,
                hidden: !FeatureConfig.IsSupportFC,
            },
            this.workSpeed,
            this.maxSpeed,
            this.portSwitch,
        ],
        disk: [
            this.locationLink,
            {
                field: 'SECTORS',
                header: this.i18nLabel.capacity,
                columnFilter: true,
                align: 'right',
                sortable: true,
                compare: (a, b, aRow, bRow): number => {
                    const aCapacity = Number(aRow.SECTORS) * Number(aRow.SECTORSIZE);
                    const bCapacity = Number(bRow.SECTORS) * Number(bRow.SECTORSIZE);
                    return aCapacity - bCapacity;
                },
                render: (value, col, data): string => this.util.adaptUnit(value, 'Sector', null, data.SECTORSIZE).label,
            },
            {
                field: 'manuCapacity',
                header: this.i18nLabel.manuCapacity,
                columnFilter: true,
                align: 'right',
                sortable: true,
                compare: compareNum,
                hidden: true,
                render: (value, col, data): string => this.tool.adaptUnit2(value, CAPACITY_UNIT.BYTE, null, data.SECTORSIZE, 1000).label,
            },
            this.id,
            {
                field: 'POOLNAME',
                header: this.i18nLabel.diskDomain,
                sortable: true,
                columnFilter: true,
                ignore: SWITCH.IS_DIRECT_MODE,
                hidden: true,
                render: (value): string => value || UNPLACEHOLDER,
            },
            {
                field: 'DISKTYPE',
                header: this.i18nLabel.type,
                render: (value: string): string => this.util.getLabelFromOptions(this.str.diskTypeRenderOpttions, Number(value)),
                filterType: 'dropdown',
                options: this.str.diskTypeOpttions,
                sortable: true,
                columnFilter: true,
                filterFn: (diskType: string, data: Disk): boolean => {
                    const dataDiskType = Number(data.DISKTYPE);
                    const conditionDiskType = Number(diskType);

                    //过滤真假两种容量型SSD
                    if (conditionDiskType === DiskTypeE.PHONY_CAPACITY_OPTIMIZED_SSD ||
                        conditionDiskType === DiskTypeE.CAPACITY_OPTIMIZED_SSD) {
                        return [DiskTypeE.PHONY_CAPACITY_OPTIMIZED_SSD, DiskTypeE.CAPACITY_OPTIMIZED_SSD].includes(dataDiskType);
                    }

                    //过滤真假两种容量型SSD_SED
                    if (conditionDiskType === DiskTypeE.PHONY_CAPACITY_OPTIMIZED_SSD_SED ||
                        conditionDiskType === DiskTypeE.CAPACITY_OPTIMIZED_SSD_SED) {
                        return [DiskTypeE.PHONY_CAPACITY_OPTIMIZED_SSD_SED, DiskTypeE.CAPACITY_OPTIMIZED_SSD_SED].includes(dataDiskType);
                    }

                    return dataDiskType === conditionDiskType;
                },
            },
            {
                field: 'LOGICTYPE',
                header: this.i18nLabel.diskRole,
                render: (value): string => this.str.diskRoleOpt[value],
                filterType: 'dropdown',
                options: this.str.diskRoleOptions,
                sortable: true,
                columnFilter: true,
            },
            {
                field: 'HEALTHSTATUS',
                header: this.i18nLabel.healthStatus,
                render: (value): any => this.healthStatusOpt[value],
                sortable: true,
                columnFilter: true,
            },
            {
                field: 'RUNNINGSTATUS',
                header: this.i18nLabel.runningStatus,
                render: (value): string => this.runningStatusOpt[value],
                sortable: true,
                columnFilter: true,
            },
            {
                field: 'ABRASIONRATE',
                header: this.i18nLabel.abrasion,
                columnFilter: true,
                sortable: true,
                render: (value: string): number => {
                    if (!value || Number(value) === -1) {
                        return -1;
                    }
                    const displayValue = Math.round((Number(value)) / 100);
                    return displayValue;
                },
                compare: compareNum,
                align: 'right',
                exportRender: (value: string): string =>
                    (!value || Number(value) === -1) ? UNPLACEHOLDER : Math.round(Number(value) / 100) + '%',
            },
            {
                field: 'REMAINLIFE',
                header: this.i18nLabel.lifeSpan,
                columnFilter: true,
                sortable: true,
                compare: compareNum,
                render: (value): string => this.tool.diskRemainLife(value),
                align: 'right',
            },
            {
                field: 'ITEM',
                header: this.i18nLabel.bomCode,
                sortable: true,
                columnFilter: true,
            },
            {
                field: 'barcode',
                header: this.i18nLabel.barcode,
                sortable: true,
                columnFilter: true,
            },
            {
                field: 'eraseProgess',
                header: this.i18nLabel.eraseProgess,
                columnFilter: true,
                render: (value): string => value ? `${value}%` : UNPLACEHOLDER,
                hidden: true,
            },
            {
                field: 'ENCRYPTDISKTYPE',
                header: this.i18nLabel.encyptType,
                columnFilter: true,
                render: (value): string => this.str.diskEncryptType[value],
                sortable: true,
                hidden: true,
            },
            {
                field: 'KEYEXPIRATIONTIME',
                header: this.i18nLabel.AKoutTime,
                columnFilter: true,
                render: (value: string): string => {
                    if (!value || Number(value) === -1) {
                        return UNPLACEHOLDER;
                    }
                    return this.renderDate(value);
                },
                sortable: true,
                hidden: true,
            },
            {
                field: 'MODEL',
                header: this.i18nLabel.diskModel,
                columnFilter: true,
                sortable: true,
                hidden: true,
            },
            {
                field: 'TEMPERATURE',
                header: this.i18nLabel.temperatureLabel,
                columnFilter: true,
                sortable: true,
                hidden: true,
                render: (value: string): string => (value && Number(value) !== TEMPERATURE_INVALID_VALUE) ? value : UNPLACEHOLDER,
                align: 'right',
            },
        ],
        lifPort: [
            {
                ...this.nameLink,
                width: undefined,
                minWidth: 200,
            },
            this.id,
            this.lifPortRunningStatus,
            {
                field: 'OPERATIONALSTATUS',
                header: this.i18nLabel.activeStatusLabel,
                columnFilter: true,
                filterType: 'dropdown',
                options: this.str.lifActiveOptions,
                render: (value): string => this.str.lifActiveOpt[value] || UNPLACEHOLDER,
            },
            {
                field: 'ROLE',
                header: this.i18nLabel.role,
                columnFilter: true,
                hidden: true,
                render: (value): string => this.str.lifRoleOpt[value] || UNPLACEHOLDER,
            },
            {
                field: 'SUPPORTPROTOCOL',
                header: this.i18nLabel.dataProtocol,
                columnFilter: true,
                render: (value): string => this.str.lifProtocolOpt[value] || UNPLACEHOLDER,
            },
            {
                field: 'IPV4ADDR',
                header: this.i18nLabel.ip,
                columnFilter: true,
                filterType: 'search',
                render: (value, col, data): string => value || data.IPV6ADDR,
            },
            {
                field: 'IPV4MASK',
                header: this.i18nLabel.subnetMaskOrPrefix,
                columnFilter: true,
                filterType: 'search',
                render: (value, col, data): string => data.IPV4ADDR ? value : data.IPV6MASK,
            },
            {
                field: 'IPV4GATEWAY',
                header: this.i18nLabel.gateway,
                columnFilter: true,
                filterType: 'search',
                render: (value, col, data): string => data.IPV4ADDR ?
                    (value || UNPLACEHOLDER) :
                    (data.IPV6GATEWAY || UNPLACEHOLDER),
            },
            {
                field: 'HOMEPORTNAME',
                header: SWITCH.SUPPORT_NAS ?
                    `${this.i18nLabel.homePort}/${this.i18nLabel.currentPort}` :
                    this.portName,
                columnFilter: true,
                filterType: 'search',
                sortable: true,
                searchPlaceholder: SWITCH.SUPPORT_NAS ? this.i18nLabel.homePortSearchPlaceholder : '',
                exportRender: (value: string, col, data: LifPort): string => data.CURRENTPORTNAME === '--' || value === data.CURRENTPORTNAME ?
                    value : `${value}/${data.CURRENTPORTNAME}`,
            },
            // 微存储屏蔽当前端口【不支持漂移组，不支持漂移】
            {
                field: 'CURRENTPORTNAME',
                header: this.i18nLabel.currentPort,
                columnFilter: true,
                filterType: 'search',
                hidden: true,
                ignore: FeatureConfig.IsHideLogicPortCurrentPort,
                sortable: true,
            },
            {
                field: 'HOMECONTROLLERID',
                header: SWITCH.SUPPORT_NAS ?
                    `${this.i18nLabel.homeController}/${this.i18nLabel.currentController}` :
                    this.i18nLabel.controller,
                columnFilter: true,
                hidden: true,
                render: (value, col, data): string => value === data.CURRENTCONTROLLERID ?
                    value :
                    `${value}/${data.CURRENTCONTROLLERID}`,
            },
            {
                field: 'FAILOVERGROUPNAME',
                header: this.i18nLabel.failoverGroupLabel,
                hidden: true,
                columnFilter: true,
                ignore: !SWITCH.SUPPORT_NAS,
            },
            {
                field: 'HOMESITEWWN',
                header: this.i18nLabel.wwnLabel,
                columnFilter: true,
                // 微存储屏蔽归属站点
                ignore: !FeatureConfig.IsShowLIFHomeSite,
                render: (value: string): string => {
                    const systemInfo = this.globalDataService.getSystemInfo();
                    const wwn = systemInfo?.wwn;
                    if (value && wwn) {
                        return value === wwn ? this.i18nLabel.localPortLabel : this.i18nLabel.remotePortLabel;
                    } else {
                        return UNPLACEHOLDER;
                    }
                },
            },
        ],
        bondPort: [
            {
                ...this.nameLink,
                width: undefined,
                minWidth: 200,
            },
            {
                ...this.id,
                filterType: 'equalSearch',
            },
            this.bondPortHealthStatus,
            this.bondPortRunningStatus,
            this.ipv4AndMask,
            this.ipv6AndPrefix,
            {
                ...this.mtu,
                filterType: 'equalSearch',
            },
            {
                field: 'PORTIDLIST',
                header: this.i18nLabel.portNum,
                columnFilter: true,
                align: 'right',
                render: (value: string): number => {
                    let portList = [];
                    try {
                        portList = JSON.parse(value);
                    } catch (error) {
                        // 异常处理
                    }

                    return portList.length;
                },
            },
            // Bond端口类型-适用计算型存储支持的全大写字段-BONDPORTTYPE
            {
                field: 'BONDPORTTYPE',
                header: this.i18nLabel.bondPortType,
                filterType: 'dropdown',
                sortable: true,
                options: this.str.bondPortOptions,
                ignore: !FeatureConfig.IsShowBondPortType,
                render: (value: string): string => {
                    const option = this.str.bondPortOptions.find((item: SelectOption) => item.value === value);
                    return option ? option.label : UNPLACEHOLDER;
                },
            },
            // Bond端口类型-适用dorado和新融合小驼峰字段-由VXLAN开关控制-bondPortType
            {
                field: 'bondPortType',
                header: this.i18nLabel.portType,
                columnFilter: true,
                filterType: 'dropdown',
                sortable: true,
                options: this.str.bondPortCamelCaseOptions,
                ignore: !FeatureConfig.IsSupportVxlan,
                render: (value: string): string => {
                    const option = this.str.bondPortCamelCaseOptions.find((item: SelectOption) => item.value === value);
                    return option ? option.label : UNPLACEHOLDER;
                },
            },
            {
                field: 'BONDMODE',
                header: this.i18nLabel.bondMode,
                filterType: 'dropdown',
                sortable: true,
                options: this.str.bondPortModesOptions,
                ignore: !FeatureConfig.IsShowBondMode,
                render: (value: string): string => {
                    const option = this.str.bondPortModesOptions.find((item: SelectOption) => item.value === value);
                    return option ? option.label : UNPLACEHOLDER;
                },
            },
            {
                field: 'numberOfInitiators',
                header: this.i18nLabel.initiatorNum,
                columnFilter: true,
                align: 'right',
            },
        ],
        vlanPort: [
            {
                ...this.name,
                width: undefined,
            },
            {
                field: 'TAG',
                header: 'ID',
                width: 80,
                align: 'right',
                columnFilter: true,
                filterType: 'equalSearch',
                sortable: true,
                compare: (a, b): number => (Number(a) - b),
            },
            this.vlanRunningStatus,
            {
                ...this.mtu,
                filterType: 'equalSearch',
            },
        ],
    };

    errorColumnDefs: { [key: string]: ColumnDef[] } = {
        fcPort: [
            this.nameForError,
            this.location,
            this.portId,
            this.linkStatus,
            {
                field: 'LOSTSIGNALS',
                header: this.i18nLabel.lostSignals,
                columnFilter: true,
                sortable: true,
                compare: compareNum,
                render: (value): string => this.renderInt(value),
            },
            {
                field: 'BADCHARNUMBER',
                header: this.i18nLabel.linkBitErrors,
                columnFilter: true,
                sortable: true,
                compare: compareNum,
                render: (value): string => this.renderInt(value),
            },
            {
                field: 'LOSTSYNC',
                header: this.i18nLabel.lostSync,
                columnFilter: true,
                sortable: true,
                compare: compareNum,
                render: (value): string => this.renderInt(value),
            },
            {
                field: 'LINKFAIL',
                header: this.i18nLabel.faildConnections,
                columnFilter: true,
                sortable: true,
                compare: compareNum,
                render: (value): string => this.renderInt(value),
            },
            {
                field: 'BADCRCNUM',
                header: this.i18nLabel.crcErrors,
                columnFilter: true,
                sortable: true,
                compare: compareNum,
                render: (value): string => this.renderInt(value),
            },
            {
                field: 'endOfFrameErrors',
                header: this.i18nLabel.frameEndSignErrors,
                columnFilter: true,
                sortable: true,
                compare: compareNum,
                render: (value): string => this.renderInt(value),
            },
            this.startTime,
        ],
        ethPort: [
            this.nameForError,
            this.location,
            this.portId,
            this.linkStatus,
            {
                field: 'ERRORPACKETS',
                header: this.i18nLabel.errorPackets,
                columnFilter: true,
                sortable: true,
                compare: compareNum,
                render: (value): string => this.renderInt(value),
            },
            {
                field: 'LOSTPACKETS',
                header: this.i18nLabel.lostPackets,
                columnFilter: true,
                sortable: true,
                compare: compareNum,
                render: (value): string => this.renderInt(value),
            },
            {
                field: 'OVERFLOWEDPACKETS',
                header: this.i18nLabel.overflowedPackets,
                columnFilter: true,
                sortable: true,
                compare: compareNum,
                render: (value): string => this.renderInt(value),
            },
            {
                field: 'crcErrors',
                header: this.i18nLabel.crcErrors,
                columnFilter: true,
                sortable: true,
                compare: compareNum,
                render: (value): string => this.renderInt(value),
            },
            {
                field: 'frameErrors',
                header: this.i18nLabel.frameErrors,
                columnFilter: true,
                sortable: true,
                compare: compareNum,
                render: (value): string => this.renderInt(value),
            },
            {
                field: 'frameLengthErrors',
                header: this.i18nLabel.frameLengthErrors,
                columnFilter: true,
                sortable: true,
                compare: compareNum,
                render: (value): string => this.renderInt(value),
            },
            this.startTime,
        ],
        rocePort: [
            this.nameForError,
            this.location,
            this.portId,
            this.linkStatus,
            {
                field: 'ERRORPACKETS',
                header: this.i18nLabel.errorPackets,
                columnFilter: true,
                sortable: true,
                compare: compareNum,
                render: (value): string => this.renderInt(value),
            },
            {
                field: 'LOSTPACKETS',
                header: this.i18nLabel.lostPackets,
                columnFilter: true,
                sortable: true,
                compare: compareNum,
                render: (value): string => this.renderInt(value),
            },
            {
                field: 'OVERFLOWEDPACKETS',
                header: this.i18nLabel.overflowedPackets,
                columnFilter: true,
                sortable: true,
                compare: compareNum,
                render: (value): string => this.renderInt(value),
            },
            {
                field: 'crcErrors',
                header: this.i18nLabel.crcErrors,
                columnFilter: true,
                sortable: true,
                compare: compareNum,
                render: (value): string => this.renderInt(value),
            },
            {
                field: 'frameErrors',
                header: this.i18nLabel.frameErrors,
                columnFilter: true,
                sortable: true,
                compare: compareNum,
                render: (value): string => this.renderInt(value),
            },
            {
                field: 'frameLengthErrors',
                header: this.i18nLabel.frameLengthErrors,
                columnFilter: true,
                sortable: true,
                compare: compareNum,
                render: (value): string => this.renderInt(value),
            },
            this.startTime,
        ],
        sasPort: [
            this.nameForError,
            this.location,
            this.portId,
            this.linkStatus,
            {
                field: 'INVALIDDWORD',
                header: this.i18nLabel.invalidDwords,
                columnFilter: true,
                sortable: true,
                compare: compareNum,
                render: (value): string => this.renderInt(value),
            },
            {
                field: 'DISPARITYERROR',
                header: this.i18nLabel.ConsistencyErrors,
                columnFilter: true,
                sortable: true,
                compare: compareNum,
                render: (value): string => this.renderInt(value),
            },
            {
                field: 'LOSSDWORD',
                header: this.i18nLabel.lostSyncDwords,
                columnFilter: true,
                sortable: true,
                compare: compareNum,
                render: (value): string => this.renderInt(value),
            },
            {
                field: 'PHYRESETERRORS',
                header: this.i18nLabel.PHYRestFailures,
                columnFilter: true,
                sortable: true,
                compare: compareNum,
                render: (value): string => this.renderInt(value),
            },
            this.startTime,
        ],
    };

    constructor(
        private str: HardwareStrService,
        private detailService: HardwareDetailService,
        private util: UtilService,
        private tool: ToolService,
        private event: EventService,
        private time: TimeService,
        private globalDataService: GlobalDataService,
        private i18nService: I18nService,
    ) { }

    /**
     * 获取Fc端口列
     */
    getFcPortColumnDefs(): ColumnDef[] {
        return [
            this.hiddenName,
            this.locationLink,
            this.fcPortHealthStatus,
            this.fcPortRunningStatus,
            {
                field: 'WWN',
                header: this.i18nLabel.WWPN,
                columnFilter: FeatureConfig.IsSupportFC,
                hidden: !FeatureConfig.IsSupportFC,
            },
            this.workSpeed,
            this.maxSpeed,
            {
                field: 'FCRUNMODE',
                header: this.i18nLabel.runningModel,
                columnFilter: true,
                render: (value): string => this.str.fcRunMode[value] || UNPLACEHOLDER,
            },
            this.portSwitch,
            // A800屏蔽iscsi
            {
                ...this.initiatorNum,
                ignore: !this.tool.supportSmartThinLicense(),
            },
            {
                field: 'protocol',
                header: this.i18nLabel.protocolTypeLab,
                columnFilter: true,
                filterType: 'dropdown',
                options: this.str.fcProtocolOpt,
                render: (value): string => this.str.fcProtocolOpt[value] || UNPLACEHOLDER,
            },
            {
                field: 'fastWriteEnable',
                header: this.i18nLabel.immediateDataLab,
                hidden: true,
                columnFilter: true,
                ignore: FeatureConfig.IsHideFcImmediateData,
                options: this.str.fastWriteEnableOpt,
                render: (value): string => this.str.fastWriteEnableOpt[value],
            },
        ];
    }

    renderInt(value: string): string {
        if (!value || Number(value) === -1) {
            return UNPLACEHOLDER;
        }
        return value;
    }

    renderDate(valueStr: string): string {
        const value = Number(valueStr);
        if (isNaN(value)) {
            return UNPLACEHOLDER;
        }

        return this.time.formatUTC(value * 1000, TIME_FORMATTER.DATE);
    }

    renderTime(valueStr: string): string {
        const value = Number(valueStr);
        if (isNaN(value) || value === 0) {
            return UNPLACEHOLDER;
        }

        return this.time.formatUTC(value * 1000);
    }

    generateOpt(obj: { [key: string]: { value: number; i18n: string } }): any {
        const res = {};
        Object.keys(obj).forEach((key) => {
            res[obj[key].value] = this.str.get(obj[key].i18n);
        });

        return res;
    }

    /**
     * 根据配置的枚举值过滤对象。
     */
    genOptByValues(obj: { [key: string]: { value: number; i18n: string } }, values: number[] = []): Array<{ label: any; value: number }> {
        const res = {};
        Object.keys(obj).forEach((key) => {
            res[obj[key].value] = this.str.get(obj[key].i18n);
        });

        return values.map((value) => ({
            label: res[value],
            value,
        }));
    }
}
